import React, { useState, useRef, useEffect } from "react";
import Editor from "@monaco-editor/react";
import { FaPlay, FaLock } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { runCodeForPlayground } from "../../features/competition/competitionSlice";
import { getUser } from "../../features/auth/authSlice";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const DebugOutput = ({ logs, isLoading, compileProblem }) => {
  const [allLogs, setAllLogs] = useState([]);

  useEffect(() => {
    if (logs) {
      setAllLogs((prev) => [
        ...prev,
        {
          timestamp: new Date().toISOString(),
          content: logs,
        },
      ]);
    }
  }, [logs]);

  const parseLogLine = (line) => {
    const regex =
      /(\d{2}:\d{2}:\d{2}\.\d{2})\s*\(\d+\)\|([^|]+)\|(?:\[(\d+)\]\|)?([^|]*)(?:\|(.*))?/;
    const match = line.match(regex);
    if (!match) return null;
    return {
      timestamp: match[1],
      type: match[2],
      level: match[4],
      message: match[5],
    };
  };

  const processLogEntry = (logContent) => {
    return logContent
      .split("\n")
      .filter(
        (line) =>
          line.includes("USER_DEBUG") &&
          !line.includes("CAMP_APEX_TEST_NO") &&
          line.trim()
      )
      .map(parseLogLine)
      .filter((log) => log !== null);
  };

  const renderLogLine = (log) => (
    <div className="leading-5 mb-1">
      <span className="text-blue-400">{log.timestamp}</span>
      <span className="text-gray-500"> | </span>
      <span
        className={
          log.type.includes("DEBUG")
            ? "text-green-400"
            : log.type.includes("ERROR")
            ? "text-red-400"
            : "text-yellow-400"
        }
      >
        {log.type}
      </span>
      {log.message && (
        <>
          <span className="text-gray-500"> | </span>
          <span className="text-gray-200">{log.message}</span>
        </>
      )}
    </div>
  );

  if (!allLogs.length && !isLoading && !compileProblem) {
    return (
      <div className="bg-zinc-900 text-gray-500 h-full font-mono text-sm p-4">
        Run your code to see debug logs here
      </div>
    );
  }

  return (
    <div className="bg-zinc-900 text-gray-100 h-full font-mono text-sm p-4 overflow-y-auto border-l border-zinc-800">
      {compileProblem && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded text-red-300">
          <div className="font-semibold mb-1">Compilation Error:</div>
          <div>{compileProblem}</div>
        </div>
      )}
      {allLogs.map((entry, sessionIdx) => (
        <div key={sessionIdx} className="mb-6">
          <div className="text-xs text-gray-500 mb-2 border-b border-gray-800 pb-1">
            Execution {sessionIdx + 1} -{" "}
            {new Date(entry.timestamp).toLocaleTimeString()}
          </div>
          {processLogEntry(entry.content).map((log, lineIdx) => (
            <div key={`${sessionIdx}-${lineIdx}`}>{renderLogLine(log)}</div>
          ))}
        </div>
      ))}
      {isLoading && (
        <div className="flex items-center gap-2 text-gray-400">
          <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent" />
          Executing...
        </div>
      )}
    </div>
  );
};

const Playground = () => {
  const [solution, setSolution] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const editorRef = useRef(null);
  const dispatch = useDispatch();

  const { debugLogs, compileProblem } = useSelector(
    (state) => state.competitions
  );
  const { user, fetchingUser } = useSelector((state) => state.auth);
  
  const isAuthenticated = user && user[0]?.instanceUrl;

  useEffect(() => {
    dispatch(getUser());
  }, [dispatch]);

  const handleEditorDidMount = (editor) => {
    editorRef.current = editor;
    setSolution("// Write your code here");
  };

  const handleEditorBeforeMount = (monaco) => {
    monaco.editor.defineTheme("modern-dark", {
      base: "vs-dark",
      inherit: true,
      rules: [{ token: "type", foreground: "569cd6" }],
      colors: {
        "editor.background": "#1e1e1e",
        "editor.foreground": "#d4d4d4",
        "editor.lineHighlightBackground": "#2a2a2a",
        "editorCursor.foreground": "#d4d4d4",
        "editor.selectionBackground": "#404040",
      },
    });
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      await dispatch(runCodeForPlayground({ apexBody: solution.trim() }));
    } catch (error) {
      console.error("Error executing code:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 mt-16">
      <Helmet>
        <title>Apex Playground | Salesforce Code Compiler</title>
        <meta name="description" content="Online Apex code compiler and debugger for Salesforce development. Test and run your Apex code in a sandbox environment." />
        <meta name="keywords" content="Apex, Salesforce, Code Compiler, Apex Playground, Salesforce Development" />
        <meta property="og:title" content="Apex Playground | Salesforce Code Compiler" />
        <meta property="og:description" content="Online Apex code compiler and debugger for Salesforce development." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Apex Playground | Salesforce Code Compiler" />
        <meta name="twitter:description" content="Online Apex code compiler and debugger for Salesforce development." />
      </Helmet>
      <h1 className="text-3xl font-bold text-center text-zinc-500 mb-8">
        Apex Compiler for Salesforce experiments
      </h1>
      <div className="h-[600px] bg-[#1e1e1e] flex flex-col rounded-lg overflow-hidden shadow-xl">
        <div className="flex-1 flex min-h-0">
          <div className="w-1/2 border-r border-zinc-800 overflow-hidden">
            <Editor
              onChange={setSolution}
              onMount={handleEditorDidMount}
              beforeMount={handleEditorBeforeMount}
              defaultLanguage="apex"
              theme="modern-dark"
              defaultValue="// Write your code here"
              options={{
                fontSize: 14,
                minimap: { enabled: false },
                scrollbar: { alwaysConsumeMouseWheel: false },
                scrollBeyondLastLine: false,
                padding: { top: 16 },
                lineNumbers: "on",
                glyphMargin: false,
                folding: true,
                lineDecorationsWidth: 0,
                lineNumbersMinChars: 3,
              }}
            />
          </div>
          <div className="w-1/2 overflow-hidden">
            <DebugOutput
              logs={debugLogs}
              isLoading={isLoading}
              compileProblem={compileProblem}
            />
          </div>
        </div>
        <div className="border-t border-zinc-800 p-2 flex justify-between items-center">
          {!isAuthenticated && !fetchingUser && (
            <div className="text-amber-400 text-sm flex items-center gap-2 ml-4">
              <FaLock size={12} />
              <span>Login required to run code.</span>
              <Link
                to="/auth"
                className="ml-2 text-blue-400 hover:text-blue-300 underline transition-colors"
              >
                Connect to an Org
              </Link>
            </div>
          )}
          {fetchingUser && (
            <div className="text-gray-400 text-sm flex items-center gap-2 ml-4">
              <div className="animate-spin h-4 w-4 border-2 border-gray-500 rounded-full border-t-transparent" />
              <span>Checking authentication...</span>
            </div>
          )}
          {isAuthenticated && <div className="ml-4" />}
          <button
            onClick={handleSubmit}
            disabled={isLoading || !isAuthenticated}
            className={`bg-blue-500 hover:bg-blue-600 text-white px-4 py-1 rounded text-sm flex items-center gap-2 transition-colors ${
              isLoading || !isAuthenticated ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {!isAuthenticated ? (
              <>
                <FaLock size={10} />
                Sign in to Run
              </>
            ) : isLoading ? (
              <>
                <div className="animate-spin h-3 w-3 border-2 border-white rounded-full border-t-transparent" />
                Running...
              </>
            ) : (
              <>
                <FaPlay size={10} />
                Run
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Playground;